import React from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Section from '../components/Section';
import { Target, Eye, Heart, CheckCircle, History, Globe } from 'lucide-react';
import './About.css';

// Import images if available, otherwise use placeholders or define paths
// Since we don't have all exact matches mapped yet, we'll use a helper or direct paths
const LeaderCard = ({ name, role, photo }) => (
    <div className="leader-card">
        <div className="leader-photo" style={{ backgroundImage: `url("${photo}")` }}></div>
        <div className="leader-info">
            <h3>{name}</h3>
            {role && <p>{role}</p>}
        </div>
    </div>
);

const About = () => {
    return (
        <Layout>
            <SEO title="About Us" description="Learn about PAOZ Hope Community Church, our mission, vision, and core values." />
            <div className="page-hero" style={{ backgroundImage: 'url("/Images/image.png")' }}>
                <div className="container">
                    <h1 className="animate-fade-in-up">About Us</h1>
                    <p className="animate-fade-in-up delay-100">Knowing Christ, Making Him Known, Changing the World</p>
                </div>
            </div>

            <Section>
                <div className="mission-grid">
                    <div className="mission-card hover-lift animate-fade-in-up delay-100">
                        <div className="card-icon"><Target size={40} /></div>
                        <h2>Our Mission</h2>
                        <p>We are a Bible-based Pentecostal Church passionately fulfilling the great commission, and engaged in social transformation.</p>
                    </div>
                    <div className="mission-card active hover-lift animate-fade-in-up delay-200">
                        <div className="card-icon"><Eye size={40} /></div>
                        <h2>Our Vision</h2>
                        <p>To know Christ, to make Him known, and to change the world.</p>
                    </div>
                    <div className="mission-card hover-lift animate-fade-in-up delay-300">
                        <div className="card-icon"><Heart size={40} /></div>
                        <h2>Our Core Values</h2>
                        <ul className="values-list">
                            <li><CheckCircle size={14} className="value-icon" /> People</li>
                            <li><CheckCircle size={14} className="value-icon" /> Integrity</li>
                            <li><CheckCircle size={14} className="value-icon" /> Fellowship</li>
                            <li><CheckCircle size={14} className="value-icon" /> Excellence</li>
                            <li><CheckCircle size={14} className="value-icon" /> Discipleship</li>
                            <li><CheckCircle size={14} className="value-icon" /> Accountability</li>
                            <li><CheckCircle size={14} className="value-icon" /> Sound Doctrine</li>
                            <li><CheckCircle size={14} className="value-icon" /> Pentecostal Heritage</li>
                        </ul>
                    </div>
                </div>
            </Section>

            <Section className="history-section">
                <div className="container">
                    <div className="grid-2 align-center">
                        <div className="history-content animate-fade-in-up">
                            <div className="section-badge"><History size={16} /> Our Story</div>
                            <h2 className="premium-title">Our Journey</h2>
                            <p>
                                Hope Community Church (HCC) began in October 2001, birthed by a small group of committed believers who had been part of Upper Room. Under the pioneering leadership of Rev. and Mrs. Cornelius, this handful of believers laid the foundation for a ministry built on prayer, fellowship, and a passion for reaching the community with the gospel. From its earliest days, HCC positioned itself as a place of hope, restoration, and spiritual growth.
                            </p>
                            <p>
                                Over the years, the church has continued to serve the Mpinga community faithfully, growing in both influence and impact. HCC also functions as the discipleship arm of VOH, shaping lives through teaching, mentoring, and spiritual formation. With a strong emphasis on church planting and discipleship, HCC has remained committed to raising leaders and expanding the reach of the gospel. Since 2002, the church has been led by Dr. Bishop Zowa, whose vision and pastoral leadership have strengthened the church's mission and guided it through seasons of growth and transformation.
                            </p>
                        </div>
                        <div className="history-image animate-fade-in-scale">
                            <img src="/Images/zimbabwe 2007 085.jpg" alt="Church History" className="shadow-premium" style={{ borderRadius: '24px', position: 'relative', zIndex: 2 }} />
                        </div>
                    </div>
                </div>
            </Section>

            <Section className="affiliation-section bg-primary text-light">
                <div className="container text-center">
                    <div className="affiliation-icon-box mb-30">
                        <Globe size={48} color="var(--secondary-color)" />
                    </div>
                    <h2 className="premium-title color-white">Part of the PAOZ Family</h2>
                    <p className="max-width-800 mx-auto opacity-09" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                        We are a proud member of the <strong>Pentecostal Assemblies of Zimbabwe (PAOZ)</strong>.
                        As part of this larger family, we are connected to more than 300 churches across the nation and
                        aligned with a global movement of believers.
                    </p>
                    <div className="paoz-stats-grid mt-50">
                        <div className="paoz-stat">
                            <h3>300+</h3>
                            <p>Churches Nationwide</p>
                        </div>
                        <div className="paoz-stat">
                            <h3>70+</h3>
                            <p>Years of Ministry</p>
                        </div>
                        <div className="paoz-stat">
                            <h3>Global</h3>
                            <p>Network & Missions</p>
                        </div>
                    </div>
                </div>
            </Section>

            <Section className="leadership-section bg-gray">
                <div className="text-center mb-50">
                    <h2 className="premium-title">Our Leadership</h2>
                    <p>Meet the team serving the PAOZ HCC family.</p>
                </div>

                <div className="leadership-category">
                    <h3 className="premium-title" style={{ fontSize: '2rem' }}>Senior Leadership</h3>
                    <div className="leaders-grid">
                        <LeaderCard
                            name="Bishop Zowa and Mrs Zowa"
                            role="Senior Pastors"
                            photo="/Images/Leaders/Bishop Zowa and Mrs Zowa.jpeg"
                        />
                        <LeaderCard
                            name="Reverend R Chimbetete and Pastor C Chimbetete"
                            role="Pastors"
                            photo="/Images/Leaders/Rev R Chimbetete and Pastor C Chimbetet.jpg"
                        />
                        <LeaderCard
                            name="Pastor S Rutsito and Deacon Rutsito"
                            role="Pastors"
                            photo="/Images/Leaders/Elder Rutsito and Pastor Rutsito.jpg"
                        />
                        <LeaderCard
                            name="Reverend L Ngoreta and Minister B Ngoreta"
                            role="Pastors"
                            photo="/Images/Leaders/Rev Ngoreta and Minister Billy.jpeg"
                        />
                        <LeaderCard
                            name="Pastor T Maseko and Mrs Maseko"
                            role="Pastor"
                            photo="/Images/placeholder.jpg"
                        />
                        <LeaderCard
                            name="Pastor Divine Ncube"
                            role="Pastor"
                            photo="/Images/Leaders/Pastor Divine.jpeg"
                        />
                        <LeaderCard
                            name="Pastor T Kahungwa"
                            role="Pastor"
                            photo="/Images/Leaders/Pastor Tashinga.jpeg"
                        />
                    </div>
                </div>

                <div className="leadership-category">
                    <h3 className="premium-title" style={{ fontSize: '2rem' }}>Elders and Deacons</h3>
                    <div className="leaders-grid compact">
                        <LeaderCard
                            name="Elder Cholasa and Mrs Cholasa"
                            role=""
                            photo="/Images/placeholder.jpg"
                        />
                        <LeaderCard
                            name="Elder Musasa and Mrs Musasa"
                            role=""
                            photo="/Images/Leaders/Elders Musasa and Mrs.jpg"
                        />
                        <LeaderCard
                            name="Elder Chekera"
                            role=""
                            photo="/Images/Leaders/Elder Chekera (2).jpg"
                        />
                        <LeaderCard
                            name="Elder Mrs Kadada"
                            role=""
                            photo="/Images/Leaders/Elder Kadada.png"
                        />
                        <LeaderCard
                            name="Elder Wisikesi"
                            role=""
                            photo="/Images/placeholder.jpg"
                        />
                        <LeaderCard
                            name="Elders Jarawaza"
                            role=""
                            photo="/Images/placeholder.jpg"
                        />
                        <LeaderCard
                            name="Elder Mbiba"
                            role=""
                            photo="/Images/Leaders/Elder Mbiba.jpeg"
                        />
                        <LeaderCard
                            name="Elder Rukuni"
                            role=""
                            photo="/Images/Leaders/Elder Rukuni.jpeg"
                        />
                        <LeaderCard
                            name="Deaconess S Chigama"
                            role=""
                            photo="/Images/Leaders/Deaconess Chigama.jpg"
                        />
                        <LeaderCard
                            name="Deacon N Nyakurimwa and Mrs Nyakurimwa"
                            role=""
                            photo="/Images/Leaders/Deacon Nigel.jpg"
                        />
                        <LeaderCard
                            name="Deacon Chekera and Mrs Chekera"
                            role=""
                            photo="/Images/Leaders/Deacon Chekera.jpg"
                        />
                        <LeaderCard
                            name="Deacon Roki and Mrs Roki"
                            role=""
                            photo="/Images/Leaders/Deacon and Roki and Mrs Roki.jpg"
                        />
                        <LeaderCard
                            name="Deaconess Purity"
                            role=""
                            photo="/Images/placeholder.jpg"
                        />
                    </div>
                </div>
            </Section>
        </Layout>
    );
};

export default About;
